import type React from "react";
import { motion } from "framer-motion";
import { WalletTransaction, WalletTransactionType, WalletTransactionStatus } from "../../../types/stores";
import { TFunction } from "i18next";
import StatsIcons from "./StatsIcons";
import { StatCard } from "./StatCard";
import { TransactionRow } from "./TransactionRow";



interface TransactionsTabProps {
  t: TFunction;
  localFilteredTransactions: WalletTransaction[];
  totalTransactions: number;
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  setDateRange: (val: { startDate: Date; endDate: Date }) => void;
  handleFilterSearch: () => void;
  transactionType: WalletTransactionType | "all";
  setTransactionType: (val: WalletTransactionType | "all") => void;
  statusFilter: WalletTransactionStatus | "all";
  setStatusFilter: (val: WalletTransactionStatus | "all") => void;
  isLoadingTransactions: boolean;
  handleResetFilters: () => void;
  handleSort: (key: keyof WalletTransaction) => void;
  sortField: keyof WalletTransaction;
  sortDirection: 'asc' | 'desc';
  renderPagination: () => React.ReactNode;
  getTransactionTypeText: (type: WalletTransactionType) => string;
  getStatusColor: (status: WalletTransactionStatus) => string;
  currentPage: number;
  totalPages: number;
}


export const renderTransactionsTab = ({
  t,
  localFilteredTransactions,
  totalTransactions,
  searchQuery,
  setSearchQuery,
  handleFilterSearch,
  transactionType,
  setTransactionType,
  statusFilter,
  setStatusFilter,
  isLoadingTransactions,
  handleResetFilters,
  handleSort,
  sortField,
  sortDirection,
  renderPagination,
  getTransactionTypeText,
  getStatusColor,
  currentPage,
  totalPages,
}: TransactionsTabProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {t('wallet.transactions', 'Transactions')}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {t('wallet.manageAllTransactions', 'Manage and track all your wallet transactions')}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <StatsIcons.refresh />
              <span>{t('wallet.resetFilters', 'Reset Filters')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="p-6 bg-gray-50 border-b border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <StatsIcons.search />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('wallet.searchTransactions', 'Search transactions...')}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />
          </div>

          {/* Type Filter */}
          <select
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value as any)}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          >
            <option value="all">{t('wallet.allTypes', 'All Types')}</option>
            <option value="collect">{t('wallet.collect', 'Collect')}</option>
            <option value="withdraw">{t('wallet.withdraw', 'Withdraw')}</option>
            <option value="send">{t('wallet.send', 'Send')}</option>
            <option value="transfer">{t('wallet.transfer', 'Transfer')}</option>
            <option value="refund">{t('wallet.refund', 'Refund')}</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          >
            <option value="all">{t('wallet.allStatus', 'All Status')}</option>
            <option value="pending">{t('wallet.pending', 'Pending')}</option>
            <option value="completed">{t('wallet.completed', 'Completed')}</option>
            <option value="failed">{t('wallet.failed', 'Failed')}</option>
            <option value="cancelled">{t('wallet.cancelled', 'Cancelled')}</option>
          </select>

          {/* Filter Button */}
          <button
            onClick={handleFilterSearch}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
          >
            <StatsIcons.filter />
            <span>{t('wallet.applyFilters', 'Apply Filters')}</span>
          </button>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-sm text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
            Total: {totalTransactions}
          </span>
          <span className="text-sm text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
            Filtered: {localFilteredTransactions.length}
          </span>
          <span className="text-sm text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
            Current Page: {currentPage}
          </span>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="p-6">
        {isLoadingTransactions ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-700 font-medium">
              {t('loading', 'Loading transactions...')}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Please wait while we fetch your transactions
            </p>
          </div>
        ) : localFilteredTransactions.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <StatsIcons.transactions />
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-2">
              {t('wallet.noTransactionsFound', 'No transactions found')}
            </h4>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {t('wallet.tryAdjustingFilters', 'Try adjusting your filters or search criteria to find what you\'re looking for')}
            </p>
            <button
              onClick={handleResetFilters}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              {t('wallet.resetFilters', 'Reset All Filters')}
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      { key: 'created_at', label: t('wallet.dateTime', 'Date & Time') },
                      { key: 'type', label: t('wallet.type', 'Type') },
                      { key: 'note', label: t('wallet.description', 'Description') },
                      { key: 'status', label: t('wallet.status', 'Status') },
                      { key: 'amount', label: t('wallet.amount', 'Amount') },
                      { key: 'fee', label: t('wallet.fee', 'Fee') },
                    ].map((column) => (
                      <th
                        key={column.key}
                        onClick={() => column.key !== 'note' && handleSort(column.key as keyof WalletTransaction)}
                        className={`py-4 px-6 text-left text-sm font-semibold text-gray-700 ${column.key !== 'note' ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{column.label}</span>
                          {column.key !== 'note' && sortField === column.key && (
                            <span className="text-blue-600">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {localFilteredTransactions.map((tx) => (
                    <TransactionRow
                      key={tx.id}
                      transaction={tx}
                      getTransactionTypeText={getTransactionTypeText}
                      getStatusColor={getStatusColor}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="mt-8">
              {renderPagination()}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};
